// Delivery Service client (chat-mls.md §5, CH3). Every payload field is
// base64 on the wire (Go []byte JSON encoding); everything is ciphertext or
// routing metadata — the decrypted world begins in chatClient, never here.

export class ChatApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ChatApiError(res.status, message);
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function toB64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

export function fromB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface ConversationRow {
  id: string;
  kind: 'dm' | 'group';
  epoch: number;
  peerUserId?: string;
  maxSeq: number;
  ackedSeq: number;
}

export interface EnvelopeRow {
  seq: number;
  kind: 'commit' | 'welcome' | 'application' | 'proposal';
  senderDevice: string;
  senderUser: string;
  recipientDevice?: string;
  payload: string; // base64
  createdAt: string;
}

export interface AppendInput {
  kind: EnvelopeRow['kind'];
  payload: Uint8Array;
  parentEpoch?: number;
  recipientDevice?: string;
  addDevices?: { userId: string; deviceId: string }[];
  removeDevices?: string[];
  /** A welcome carried ATOMICALLY with an add-commit (crash-safety). */
  welcome?: Uint8Array;
  welcomeRecipient?: string;
}

export const chatApi = {
  registerDevice: (id: string, signatureKey: Uint8Array) =>
    post<{ id: string }>('/v1/chat/devices', { id, signatureKey: toB64(signatureKey) }),

  addKeyPackages: (deviceId: string, packages: { id: string; data: Uint8Array; lastResort?: boolean }[]) =>
    post<{ stored: number }>(`/v1/chat/devices/${encodeURIComponent(deviceId)}/key-packages`, {
      packages: packages.map((p) => ({ id: p.id, data: toB64(p.data), lastResort: p.lastResort ?? false })),
    }),

  /** Consumes one package per device of the target — call only when adding. */
  claimKeyPackages: (userId: string) =>
    request<{ packages: { deviceId: string; data: string }[] }>(
      `/v1/chat/users/${encodeURIComponent(userId)}/key-packages`,
    ),

  createConversation: (id: string, device: string, kind: 'dm' | 'group', peerUserId?: string) =>
    post<ConversationRow>('/v1/chat/conversations', { id, device, kind, peerUserId }),

  listConversations: (device: string) =>
    request<{ conversations: ConversationRow[] }>(
      `/v1/chat/conversations?device=${encodeURIComponent(device)}`,
    ),

  appendEnvelope: (convId: string, device: string, input: AppendInput) =>
    post<{ seq: number }>(`/v1/chat/conversations/${encodeURIComponent(convId)}/envelopes`, {
      device,
      kind: input.kind,
      payload: toB64(input.payload),
      parentEpoch: input.parentEpoch ?? 0,
      recipientDevice: input.recipientDevice ?? '',
      addDevices: input.addDevices ?? [],
      removeDevices: input.removeDevices ?? [],
      welcome: input.welcome ? toB64(input.welcome) : '',
      welcomeRecipient: input.welcomeRecipient ?? '',
    }),

  listEnvelopes: (convId: string, device: string, afterSeq: number) =>
    request<{ envelopes: EnvelopeRow[]; epoch: number }>(
      `/v1/chat/conversations/${encodeURIComponent(convId)}/envelopes?device=${encodeURIComponent(device)}&afterSeq=${afterSeq}`,
    ),

  roster: (convId: string, device: string) =>
    request<{ devices: { userId: string; deviceId: string }[] }>(
      `/v1/chat/conversations/${encodeURIComponent(convId)}/roster?device=${encodeURIComponent(device)}`,
    ),

  claimDeviceKeyPackage: (deviceId: string) =>
    request<{ data: string }>(`/v1/chat/devices/${encodeURIComponent(deviceId)}/key-package`),

  fileUploadUrl: (convId: string, device: string, fileId: string, size: number) =>
    post<{ uploadUrl: string; objectKey: string }>(
      `/v1/chat/conversations/${encodeURIComponent(convId)}/files/upload`,
      { device, fileId, size },
    ),

  fileDownloadUrl: (convId: string, device: string, objectKey: string) =>
    post<{ downloadUrl: string }>(
      `/v1/chat/conversations/${encodeURIComponent(convId)}/files/download`,
      { device, objectKey },
    ),

  ack: (device: string, conversationId: string, seq: number) =>
    post<{ ok: boolean }>('/v1/chat/ack', { device, conversationId, seq }),
};
